/**
 * StaffTimesheetTab (11F.7) — ref `staff2.png`.
 *
 * Every figure here is DERIVED from the sessions the staff member taught
 * (`GET /v1/staff/{id}/timesheet`); there is no stored timesheet document.
 * That is the point: a session edited on the calendar shows up here
 * immediately, with no sync step that could drift.
 *
 * Occupancy is shown only when the staff member has configured working
 * hours. Without them there is no denominator, and inventing one (a
 * 40-hour week, say) would produce a confident-looking number that means
 * nothing — so the card says so instead.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Card, Dropdown, Table } from '@/components/common';
import { useStaffTimesheet, type TimesheetRow } from '@features/people/api/staff';

/** Table<T> keys rows by `id`; the timesheet's natural key is session_id. */
type TimesheetTableRow = TimesheetRow & { id: string };

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Stat = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatValue = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-variant-numeric: tabular-nums;
`;

const StatHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-top: 2px;
`;

const Bar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const PERIODS = [
  { value: '30', label: 'Last 30 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
] as const;

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export const StaffTimesheetTab: React.FC<{ staffId: string }> = ({ staffId }) => {
  const [days, setDays] = useState('30');
  const from = isoDaysAgo(Number(days));
  const to = new Date().toISOString().slice(0, 10);

  const { data, isLoading, isError } = useStaffTimesheet(staffId, from, to);

  return (
    <div data-testid="candeza-staff-timesheet-tab">
      <Bar>
        <Dropdown
          label="Period"
          options={PERIODS.map((p) => ({ value: p.value, label: p.label }))}
          value={days}
          onChange={setDays}
          testId="candeza-staff-timesheet-period"
        />
      </Bar>

      <Stats>
        <Stat>
          <StatLabel>Classes taken</StatLabel>
          <StatValue data-testid="candeza-staff-timesheet-classes">
            {data?.classes_taken ?? '—'}
          </StatValue>
        </Stat>
        <Stat>
          <StatLabel>Hours taught</StatLabel>
          <StatValue data-testid="candeza-staff-timesheet-hours">
            {data ? data.hours_taught.toFixed(2) : '—'}
          </StatValue>
        </Stat>
        <Stat>
          <StatLabel>Hours available</StatLabel>
          <StatValue>{data?.hours_available?.toFixed(2) ?? '—'}</StatValue>
          {data && data.hours_available === null && (
            <StatHint>No working hours set</StatHint>
          )}
        </Stat>
        <Stat>
          <StatLabel>Occupancy</StatLabel>
          <StatValue data-testid="candeza-staff-timesheet-occupancy">
            {data?.occupancy_pct !== null && data?.occupancy_pct !== undefined
              ? `${data.occupancy_pct}%`
              : '—'}
          </StatValue>
          {data && data.occupancy_pct === null && (
            <StatHint>Needs working hours to calculate</StatHint>
          )}
        </Stat>
        <Stat>
          <StatLabel>Students taught</StatLabel>
          <StatValue>{data?.total_students ?? '—'}</StatValue>
        </Stat>
      </Stats>

      <Card padding="lg">
        {isError ? (
          <p style={{ color: '#dc2626' }}>Could not load the timesheet for this period.</p>
        ) : (
          <Table<TimesheetTableRow>
            data={(data?.rows ?? []).map((r) => ({ ...r, id: r.session_id }))}
            isLoading={isLoading}
            emptyMessage="No classes taught in this period."
            testId="candeza-staff-timesheet-table"
            getRowTestId={(row) => `candeza-staff-timesheet-row-${row.session_id}`}
            columns={[
              {
                key: 'date',
                header: 'Date',
                isPrimary: true,
                render: (r) =>
                  new Date(r.date).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  }),
              },
              { key: 'day_of_week', header: 'Day', hideOnMobile: true, render: (r) => r.day_of_week },
              {
                key: 'time',
                header: 'Time',
                render: (r) =>
                  new Date(r.starts_at).toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
              },
              {
                key: 'duration_minutes',
                header: 'Duration',
                render: (r) => `${r.duration_minutes} min`,
              },
              {
                key: 'subject_name',
                header: 'Class',
                render: (r) => r.subject_name ?? '—',
              },
              {
                key: 'branch_id',
                header: 'Location',
                hideOnMobile: true,
                render: (r) => r.branch_id ?? '—',
              },
              {
                key: 'student_count',
                header: 'Students',
                render: (r) => r.student_count,
              },
              {
                key: 'attendance_marked',
                header: 'Attendance',
                hideOnMobile: true,
                // Hours count either way — this flags a record-keeping gap,
                // not a payroll one.
                render: (r) =>
                  r.attendance_marked ? (
                    <span style={{ color: '#15803d' }}>Marked</span>
                  ) : (
                    <span style={{ color: '#b45309' }}>Not marked</span>
                  ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default StaffTimesheetTab;
